/**
 * App analytics — typed helpers for the key product flows.
 *
 * Thin wrapper over `trackFunnelEvent` (persisted to public.funnel_events)
 * and `trackEvent` (local/dev console + future provider hook), so call sites
 * stay typo-free and every flow event carries a consistent payload.
 *
 * Every helper is fire-and-forget and never throws.
 */
import { trackFunnelEvent, type FunnelEventName } from "@/lib/funnelAnalytics";
import { trackEvent, captureError } from "@/lib/observability";
import { reportError } from "@/lib/errorMonitoring";

const ONCE_PREFIX = "myelina.analytics.once.";

/** Returns true the first time a key is seen in this browser session. */
function firstTimeThisSession(key: string): boolean {
  try {
    const storageKey = ONCE_PREFIX + key;
    if (sessionStorage.getItem(storageKey)) return false;
    sessionStorage.setItem(storageKey, "1");
    return true;
  } catch {
    // Storage blocked (private mode, SSR): fall back to tracking every time.
    return true;
  }
}

type Props = Record<string, unknown>;

function emit(name: FunnelEventName, props: Props = {}) {
  trackEvent(name, props);
  void trackFunnelEvent(name, props);
}

/* ---------------------------------------------------------------- onboarding */

export type OnboardingFlow = "signed_in" | "public_wizard";

/** Fired once per session when a user lands on step 1 of an onboarding flow. */
export function trackOnboardingStarted(flow: OnboardingFlow, props: Props = {}) {
  if (!firstTimeThisSession(`onboarding_started.${flow}`)) return;
  emit("onboarding_started", { flow, ...props });
}

/** Fired when a user advances past an onboarding step. */
export function trackOnboardingStep(
  flow: OnboardingFlow,
  step: number,
  totalSteps: number,
  props: Props = {},
) {
  emit("onboarding_step_completed", { flow, step, total_steps: totalSteps, ...props });
}

/** Fired when a user goes back a step (signal for confusing steps). */
export function trackOnboardingStepBack(flow: OnboardingFlow, step: number) {
  emit("onboarding_step_back", { flow, step });
}

/* ---------------------------------------------------------------- predictions */

/** Fired as soon as a prediction request leaves the client. */
export function trackPredictionRequested(source: string, props: Props = {}) {
  emit("prediction_requested", { source, ...props });
}

/** Fired when a prediction comes back and is rendered. */
export function trackPredictionCreated(
  source: string,
  props: { duration_ms?: number; confidence?: number | null; blocks?: number } = {},
) {
  emit("prediction_created", { source, ...props });
}

/** Fired when a prediction request fails. Reason is a coarse bucket, not raw error text. */
export function trackPredictionFailed(source: string, reason: string, props: Props = {}) {
  emit("prediction_failed", { source, reason, ...props });
}

/** Fired once per session the first time a user actually sees a forecast. */
export function trackPredictionViewed(surface: string, props: Props = {}) {
  if (!firstTimeThisSession(`prediction_viewed.${surface}`)) return;
  emit("prediction_viewed", { surface, ...props });
}

/* ------------------------------------------------------------------ app views */

/**
 * Fired once per session per authenticated surface (dashboard, insights, ...).
 * Deduped so a session's navigation churn does not inflate visit counts.
 */
export function trackAppView(surface: string, props: Props = {}) {
  if (!firstTimeThisSession(`app_view.${surface}`)) return;
  emit("app_view", { surface, ...props });
}

/** Explicit dashboard visit helper (the most-watched surface). */
export function trackDashboardVisit(props: Props = {}) {
  trackAppView("dashboard", props);
}

/* -------------------------------------------------------- password recovery */

/** Coarse bucket for a failure reason. Never carries raw user input. */
export function classifyFailure(err: unknown): string {
  const msg = String(err instanceof Error ? err.message : err ?? "").toLowerCase();
  if (!msg) return "unknown";
  if (msg.includes("429") || msg.includes("rate limit") || msg.includes("too many")) return "rate_limited";
  if (msg.includes("402") || msg.includes("credit")) return "credits_exhausted";
  if (msg.includes("timeout") || msg.includes("timed out")) return "timeout";
  if (msg.includes("network") || msg.includes("failed to fetch")) return "network";
  if (msg.includes("expired")) return "expired";
  if (msg.includes("invalid") || msg.includes("token")) return "invalid_token";
  if (msg.includes("401") || msg.includes("unauthor")) return "unauthorized";
  return "unknown";
}

/** Reports a flow failure to the error pipeline with consistent tags. */
function report(flow: string, step: string, err: unknown, extra: Props = {}) {
  const error = err instanceof Error ? err : new Error(String(err ?? step));
  captureError(error, {
    severity: "warning",
    tags: { flow, step },
    extra,
  });
  reportError({
    message: `[${flow}] ${step}: ${error.message}`,
    stack: error.stack,
    severity: "warning",
    tags: { flow, step },
    extra,
  });
}

/** A reset email was requested from the sign-in screen. */
export function trackPasswordResetRequested(props: Props = {}) {
  emit("password_reset_requested", props);
}

/** The reset email request succeeded. `duration_ms` is the round-trip latency. */
export function trackPasswordResetEmailSent(duration_ms: number, props: Props = {}) {
  emit("password_reset_email_sent", { duration_ms, ...props });
}

/** The reset email request failed. Reported to the error pipeline too. */
export function trackPasswordResetRequestFailed(err: unknown, duration_ms: number, props: Props = {}) {
  const reason = classifyFailure(err);
  emit("password_reset_request_failed", { reason, duration_ms, ...props });
  report("password_reset", "request", err, { reason, duration_ms, ...props });
}

/** The user landed on the set-new-password screen. */
export function trackPasswordResetLinkOpened(props: Props = {}) {
  emit("password_reset_link_opened", props);
}

/** The recovery token exchange resolved into a session. */
export function trackPasswordResetLinkReady(duration_ms: number, props: Props = {}) {
  emit("password_reset_link_ready", { duration_ms, ...props });
}

/** The recovery link was rejected (explicit error, or exchange never settled). */
export function trackPasswordResetLinkInvalid(reason: string, duration_ms: number, props: Props = {}) {
  emit("password_reset_link_invalid", { reason, duration_ms, ...props });
  report("password_reset", "link_check", new Error(`recovery link unusable: ${reason}`), {
    reason,
    duration_ms,
    ...props,
  });
}

/** The new password was accepted. */
export function trackPasswordResetCompleted(duration_ms: number, props: Props = {}) {
  emit("password_reset_completed", { duration_ms, ...props });
}

/** The password update call failed. */
export function trackPasswordResetFailed(err: unknown, duration_ms: number, props: Props = {}) {
  const reason = classifyFailure(err);
  emit("password_reset_failed", { reason, duration_ms, ...props });
  report("password_reset", "update", err, { reason, duration_ms, ...props });
}

/* -------------------------------------------------------- session handling */

/** An existing session was restored on load. `duration_ms` = time to hydrate. */
export function trackSessionRestored(duration_ms: number, props: Props = {}) {
  emit("session_restored", { duration_ms, ...props });
}

/** Supabase rotated the access token. */
export function trackSessionRefreshed(props: Props = {}) {
  emit("session_refreshed", props);
}

/** The session went away without an explicit sign-out. */
export function trackSessionExpired(props: Props = {}) {
  emit("session_expired", props);
}

/** The user signed out explicitly. */
export function trackSessionSignedOut(props: Props = {}) {
  emit("session_signed_out", props);
}

/** Any session/auth infrastructure failure (hydrate, refresh, sign-out). */
export function trackSessionError(step: string, err: unknown, props: Props = {}) {
  const reason = classifyFailure(err);
  emit("session_error", { step, reason, ...props });
  report("session", step, err, { reason, ...props });
}

/* ------------------------------------------------- prediction error reports */

/** Companion to `trackPredictionFailed`: pushes the failure to error monitoring. */
export function reportPredictionFailure(source: string, err: unknown, props: Props = {}) {
  report("prediction", source, err, { reason: classifyFailure(err), ...props });
}

/* ------------------------------------------------ dashboard load performance */

/** Above this, a dashboard load counts as a regression worth alerting on. */
export const DASHBOARD_LOAD_SLOW_MS = 2500;

/** Fired when the dashboard data fetch begins. */
export function trackDashboardLoadStarted(props: Props = {}) {
  emit("dashboard_load_started", props);
}

/**
 * Fired when dashboard data has resolved. Also emits `dashboard_load_slow`
 * and an error-monitoring warning when the load exceeds the threshold.
 */
export function trackDashboardLoadCompleted(
  duration_ms: number,
  props: Props = {},
) {
  const slow = duration_ms > DASHBOARD_LOAD_SLOW_MS;
  emit("dashboard_load_completed", { duration_ms, slow, ...props });
  if (slow) {
    emit("dashboard_load_slow", {
      duration_ms,
      threshold_ms: DASHBOARD_LOAD_SLOW_MS,
      ...props,
    });
    report("dashboard", "slow_load", new Error(`Dashboard load took ${Math.round(duration_ms)}ms`), {
      duration_ms,
      threshold_ms: DASHBOARD_LOAD_SLOW_MS,
      ...props,
    });
  }
}

/** Fired when the dashboard data fetch fails; also reports to error monitoring. */
export function trackDashboardLoadFailed(err: unknown, duration_ms: number, props: Props = {}) {
  const reason = classifyFailure(err);
  emit("dashboard_load_failed", { reason, duration_ms, ...props });
  report("dashboard", "load_failed", err, { reason, duration_ms, ...props });
}


/* ------------------------------------------------------- clinician contact */

export type ClinicianPhonePlacement = "inquiry_form" | "brief_download";

/** Fired when a clinician clicks the AI phone line (tel:) on /for-clinicians. */
export function trackClinicianPhoneClick(
  placement: ClinicianPhonePlacement,
  props: Props = {},
) {
  emit("clinician_phone_click", {
    placement,
    surface: "for_clinicians",
    phone: "+18887212444",
    first_click_this_session: firstTimeThisSession(`clinician_phone_click.${placement}`),
    ...props,
  });
}


/** Test seam: clears the once-per-session dedupe keys. */
export function resetSessionAnalyticsDedupe() {
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k?.startsWith(ONCE_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}
